import React, { useEffect, useRef } from 'react';
import { Row, Col } from 'react-bootstrap';
import * as echarts from 'echarts';
import { KnowledgeHubStats } from '../../services/knowledgehub.service';
import { useAppContext } from 'providers/AppProvider';
import Nodata from 'assets/img/spot-illustrations/404-illustration.png';

interface SystemStatsProps {
  stats: KnowledgeHubStats | null;
  isVisible?: boolean;
}

const SystemStats: React.FC<SystemStatsProps> = ({
  stats,
  isVisible = true
}) => {
  const scatterChartRef = useRef<HTMLDivElement>(null);
  const scatterChartInstance = useRef<echarts.ECharts | null>(null);

  const {
    config: { isDark },
    getThemeColor
  } = useAppContext();

  useEffect(() => {
    // Solo inicializar charts si el componente es visible y hay datos
    if (!stats || !isVisible || !scatterChartRef.current) {
      return;
    }

    // Pequeño delay para asegurar que el DOM esté listo
    const timer = setTimeout(() => {
      // Verificar que los elementos tengan dimensiones
      if (scatterChartRef.current?.offsetWidth === 0) {
        return;
      }

      // Inicializar o obtener instancias existentes
      if (!scatterChartInstance.current && scatterChartRef.current) {
        scatterChartInstance.current = echarts.init(scatterChartRef.current);
      }

      // Configurar Scatter Chart
      if (scatterChartInstance.current) {
        // Preparar datos para scatter
        const scatterData = stats.collections.map(col => ({
          name: col.name,
          value: [
            col.vectorsCount, // Eje X: número de vectores
            col.documentsCount, // Eje Y: número de documentos
            col.vectorsCount // Tamaño de burbuja (basado en vectores)
          ],
          // Información adicional para el tooltip
          itemStyle: {
            opacity: 0.8
          }
        }));

        // Calcular el tamaño máximo y mínimo de las burbujas
        const maxVectors = Math.max(
          ...stats.collections.map(c => c.vectorsCount)
        );
        const minVectors = Math.min(
          ...stats.collections.map(c => c.vectorsCount)
        );

        const scatterOption: echarts.EChartsOption = {
          tooltip: {
            trigger: 'item',
            backgroundColor: !isDark
              ? 'rgba(240, 240, 240, 0.9)'
              : 'rgba(49, 55, 74, 0.9)',
            textStyle: {
              color: !isDark ? '#202020' : '#ccc',
              fontFamily: 'Nunito Sans',
              fontSize: 14
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formatter: function (params: any) {
              return `
                <strong>${params.name}</strong><br/>
                Documents: ${params.value[1]}<br/>
                Vectors: ${params.value[0].toLocaleString()}<br/>
                Avg vectors/doc: ${
                  params.value[1] > 0
                    ? Math.round(params.value[0] / params.value[1])
                    : 0
                }
              `;
            }
          },
          grid: {
            left: '40',
            right: '20',
            bottom: '30',
            top: '50',
            containLabel: true
          },
          xAxis: {
            type: 'value',
            name: 'Number of Vectors',
            nameLocation: 'middle',
            nameGap: 30,
            nameTextStyle: {
              color: getThemeColor('gray-600'),
              fontSize: 10
            },
            axisLine: {
              lineStyle: {
                color: getThemeColor('gray-200')
              }
            },
            axisLabel: {
              color: getThemeColor('gray-600'),
              formatter: function (value: number) {
                if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
                return value.toString();
              },
              fontSize: 9
            },
            splitLine: {
              lineStyle: {
                color: getThemeColor('gray-200'),
                type: 'dashed'
              }
            }
          },
          yAxis: {
            type: 'value',
            name: 'Number of Documents',
            nameLocation: 'middle',
            nameGap: 40,
            nameTextStyle: {
              color: getThemeColor('gray-600'),
              fontSize: 10
            },
            axisLine: {
              lineStyle: {
                color: getThemeColor('gray-200')
              }
            },
            axisLabel: {
              color: getThemeColor('gray-600'),
              fontSize: 9
            },
            splitLine: {
              lineStyle: {
                color: getThemeColor('gray-200'),
                type: 'dashed'
              }
            }
          },
          series: [
            {
              type: 'scatter',
              symbolSize: function (data: number[]) {
                // Escalar el tamaño de la burbuja basado en el número de vectores
                // Mínimo 20px, máximo 80px
                if (maxVectors === minVectors) return 40;

                const normalizedSize =
                  (data[2] - minVectors) / (maxVectors - minVectors);
                return 20 + normalizedSize * 60;
              },
              itemStyle: {
                color: getThemeColor('primary'),
                borderColor: getThemeColor('primary-dark'),
                borderWidth: 1,
                shadowBlur: 10,
                shadowColor: 'rgba(0, 0, 0, 0.2)'
              },
              emphasis: {
                scale: 1.2,
                itemStyle: {
                  shadowBlur: 20,
                  shadowColor: 'rgba(0, 0, 0, 0.3)'
                }
              },
              data: scatterData,
              // Agregar labels a las burbujas
              label: {
                show: true,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                formatter: function (params: any) {
                  return params.name;
                },
                position: 'bottom',
                color: getThemeColor('text-primary-darker'),
                fontSize: 11
              }
            }
          ]
        };

        scatterChartInstance.current.setOption(scatterOption);
      }
    }, 100); // Delay de 100ms

    return () => {
      clearTimeout(timer);
    };
  }, [stats, isDark, getThemeColor, isVisible]);

  // Resize handler
  useEffect(() => {
    const handleResize = () => {
      if (scatterChartInstance.current) {
        scatterChartInstance.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      if (scatterChartInstance.current) {
        scatterChartInstance.current.dispose();
        scatterChartInstance.current = null;
      }
    };
  }, []);

  if (!stats) {
    return (
      <div className="text-center py-5">
        <p className="text-body-tertiary fs-9">No statistics available!</p>
      </div>
    );
  }

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  return (
    <div>
      {/* Storage Summary */}
      <Row className="mt-3">
        <Col>
          <div>
            <Row className="align-items-center">
              <Col md={8}>
                <h4 className="text-body-emphasis mb-1">Storage Summary</h4>
                <p className="text-body-secondary mb-0 fs-9 lh-sm">
                  Your knowledge base contains {stats.totalCollections}{' '}
                  collections with {stats.totalDocuments} documents and{' '}
                  {formatNumber(stats.totalVectors)} vectors.
                  <br />
                  Average document complexity:{' '}
                  {stats.totalDocuments > 0
                    ? Math.round(stats.totalVectors / stats.totalDocuments)
                    : 0}{' '}
                  vectors per document.
                </p>
              </Col>
              <Col md={4} className="text-end">
                <small className="text-body-tertiary">
                  Last updated:{' '}
                  {new Date(stats.timestamp).toLocaleString('es-ES')}
                </small>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
      {/* Charts */}
      <Row className="mb-0">
        <Col>
          <div>
            <div>
              {stats.collections.length > 0 ? (
                <div
                  ref={scatterChartRef}
                  style={{ width: '100%', height: '250px' }}
                ></div>
              ) : (
                <p className="text-center text-body-tertiary fs-9">
                  No data available!
                  <br />
                  <img src={Nodata} alt="No Data" width={250} />
                </p>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default SystemStats;
